import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { faDownload, faFileExcel } from '@fortawesome/pro-light-svg-icons';
import { faFileUpload } from '@fortawesome/pro-solid-svg-icons';
import { filter, switchMap, take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { assert } from '../../../../core/assert/assert';
import { FileNameModel } from '../../../../core/models/file/file-name.model';
import { TodoEntryModel } from '../../../../core/models/todo-planner/todo-entry.model';
import { ShiveDialogService } from '../../../../core/services/controls/shive-dialog.service';
import { FileService } from '../../../../core/services/file.service';
import { TodoPlannerHttpService } from '../../../../core/state/todo-planner/todo-planner-http.service';
import { FileUploadDialogComponent } from '../../../../shared/components/dialogs/file-upload-dialog/file-upload-dialog.component';
import { FileEntityKey, FileEntityKind } from '../../../../shared/data-types/file-types';
import { HttpParamObject } from '../../../../shared/data-types/http-types';
import { TodoPlannerService } from '../../services/todo-planner.service';

@Component({
  selector: 'app-todo-file-upload-popup',
  templateUrl: './todo-file-upload-popup.component.html',
  standalone: false,
})
export class TodoFileUploadPopupComponent implements OnInit, OnDestroy {
  // Signals
  attachments = signal<FileNameModel[]>([]);

  public readonly faFileExcel = faFileExcel;
  public readonly faFileUpload = faFileUpload;
  public readonly faDownload = faDownload;

  // DI
  private readonly todoPlannerService = inject(TodoPlannerService);
  private readonly todoPlannerHttpService = inject(TodoPlannerHttpService);
  private readonly fileService = inject(FileService);
  private readonly shiveDialogService = inject(ShiveDialogService);

  // Misc
  private todoEntryId = -1;
  private readonly subs = new SubSink();

  ngOnInit(): void {
    this.todoPlannerService.sendTodoEntryModel$
      .pipe(
        filter((entry) => !!entry),
        switchMap((entry: TodoEntryModel) =>
          this.todoPlannerHttpService.getSingleTodoPlannerEntry(entry.id),
        ),
        take(1),
      )
      .subscribe((todoEntryModel) => {
        this.todoEntryId = todoEntryModel.id;
        this.attachments.set(todoEntryModel.attachments);
      });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public uploadFile(): void {
    assert(this.todoEntryId >= 0);

    const ref = this.shiveDialogService.open({
      title: 'Datei hochladen',
      content: FileUploadDialogComponent,
      width: 572,
    });

    ref.content.setInput('fileEntityKey', FileEntityKey.TodoId);
    ref.content.setInput('fileEntityKind', FileEntityKind.TodoAttachment);
    ref.content.setInput('fileEntityValue', this.todoEntryId);
  }

  public downloadFile(id: number): void {
    const params: HttpParamObject = {
      kind: FileEntityKind.TodoAttachment,
      [FileEntityKey.FileId]: id,
    };
    this.fileService.fetchFile(params, true);
  }
}
